import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  TreeDeciduous, ArrowLeft, Edit, Save, X, Upload, 
  Calendar, MapPin, Briefcase, Mail, BookOpen, Users, Heart
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generation labels
const GENERATION_LABELS = {
  0: "Great-grandparents",
  1: "Grandparents", 
  2: "Parents",
  3: "You / Siblings",
  4: "Children",
  5: "Grandchildren",
  6: "Great-grandchildren"
};

const GENERATION_OPTIONS = [
  { value: 0, label: "Generation 0 - Great-grandparents" },
  { value: 1, label: "Generation 1 - Grandparents" },
  { value: 2, label: "Generation 2 - Parents" },
  { value: 3, label: "Generation 3 - You / Siblings" },
  { value: 4, label: "Generation 4 - Children" },
  { value: 5, label: "Generation 5 - Grandchildren" },
  { value: 6, label: "Generation 6 - Great-grandchildren" }
];

const MemberPage = ({ user }) => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [uploading, setUploading] = useState(false);
  const [children, setChildren] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [father, setFather] = useState(null);
  const [mother, setMother] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    fetchMember();
  }, [memberId]);

  const fetchMember = async () => {
    try {
      const response = await fetch(`${API}/members/${memberId}`, {
        credentials: "include"
      });

      if (!response.ok) {
        toast.error("Member not found");
        navigate(-1);
        return;
      }

      const data = await response.json();
      setMember(data);
      setEditData(data);
      
      // Fetch the branch to check if user is admin
      const branchRes = await fetch(`${API}/branches/${data.branch_id}`, {
        credentials: "include"
      });
      if (branchRes.ok) {
        const branchData = await branchRes.json();
        setIsAdmin(branchData.is_admin);
      }
      
      // Fetch children of this member
      const childrenRes = await fetch(`${API}/members/${memberId}/children`, {
        credentials: "include"
      });
      if (childrenRes.ok) {
        const childrenData = await childrenRes.json();
        setChildren(childrenData);
      }
      
      // Fetch all members in the branch for parent selection and to display father/mother names
      const allMembersRes = await fetch(`${API}/members?branch_id=${data.branch_id}`, {
        credentials: "include"
      });
      if (allMembersRes.ok) {
        const allMembersData = await allMembersRes.json();
        setAllMembers(allMembersData);
        
        // Find father and mother
        if (data.parent_member_id) {
          const fatherData = allMembersData.find(m => m.member_id === data.parent_member_id);
          setFather(fatherData || null);
        }
        if (data.mother_member_id) {
          const motherData = allMembersData.find(m => m.member_id === data.mother_member_id);
          setMother(motherData || null);
        }
      }
    } catch (error) {
      console.error("Error fetching member:", error);
      toast.error("Failed to load member");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const response = await fetch(`${API}/members/${memberId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(editData)
      });

      if (!response.ok) throw new Error("Failed to update");

      const updated = await response.json();
      setMember(updated);
      setEditing(false);
      toast.success("Member updated!");
    } catch (error) {
      console.error("Error updating member:", error);
      toast.error("Failed to update member");
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API}/upload/photo`, {
        method: "POST",
        credentials: "include",
        body: formData
      });

      if (!response.ok) throw new Error("Upload failed");

      const data = await response.json();
      setEditData({ ...editData, photo_url: data.photo_url });
      toast.success("Photo uploaded!");
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!member) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <TreeDeciduous className="w-8 h-8 text-primary" />
              <span className="font-display text-xl font-bold text-foreground">Matambanadzo-Gurundoro</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {editing ? (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setEditing(false);
                    setEditData(member);
                  }}
                  data-testid="cancel-edit-btn"
                >
                  <X className="w-4 h-4 mr-2" /> Cancel
                </Button>
                <Button 
                  onClick={handleSave}
                  data-testid="save-member-btn"
                  className="bg-primary text-primary-foreground"
                >
                  <Save className="w-4 h-4 mr-2" /> Save
                </Button>
              </>
            ) : (
              isAdmin && (
                <Button 
                  variant="outline"
                  onClick={() => setEditing(true)}
                  data-testid="edit-member-btn"
                >
                  <Edit className="w-4 h-4 mr-2" /> Edit
                </Button>
              )
            )}
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Profile Header */}
        <Card className="bg-card rounded-2xl border border-border/60 shadow-sm mb-8">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="relative">
                <Avatar className="h-32 w-32">
                  <AvatarImage src={editing ? editData.photo_url : member.photo_url} />
                  <AvatarFallback className="bg-secondary text-secondary-foreground text-3xl">
                    {member.first_name?.charAt(0)}{member.last_name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                {editing && (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handlePhotoUpload}
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      className="absolute bottom-0 right-0 rounded-full"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="upload-photo-btn"
                    >
                      <Upload className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
              
              <div className="flex-1 text-center md:text-left">
                {editing ? (
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="space-y-2">
                      <Label>First Name</Label>
                      <Input
                        data-testid="edit-first-name"
                        value={editData.first_name || ""}
                        onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Last Name</Label>
                      <Input
                        data-testid="edit-last-name"
                        value={editData.last_name || ""}
                        onChange={(e) => setEditData({ ...editData, last_name: e.target.value })}
                      />
                    </div>
                  </div>
                ) : (
                  <h1 className="font-display text-4xl font-bold text-foreground mb-2">
                    {member.first_name} {member.last_name}
                  </h1>
                )}
                
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-muted-foreground">
                  {(editing ? editData.birth_date : member.birth_date) && (
                    <span className="flex items-center gap-1 text-sm">
                      <Calendar className="w-4 h-4" />
                      Born {editing ? editData.birth_date : member.birth_date}
                    </span>
                  )}
                  {(editing ? editData.death_date : member.death_date) && (
                    <span className="flex items-center gap-1 text-sm">
                      - Died {editing ? editData.death_date : member.death_date}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Basic Info Card */}
          <Card className="bg-card rounded-2xl border border-border/60 shadow-sm">
            <CardHeader>
              <CardTitle className="font-display text-xl">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {editing ? (
                <>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> Birth Date
                    </Label>
                    <Input
                      type="date"
                      data-testid="edit-birth-date"
                      value={editData.birth_date || ""}
                      onChange={(e) => setEditData({ ...editData, birth_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" /> Death Date
                    </Label>
                    <Input
                      type="date"
                      data-testid="edit-death-date"
                      value={editData.death_date || ""}
                      onChange={(e) => setEditData({ ...editData, death_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4" /> Occupation
                    </Label>
                    <Input
                      data-testid="edit-occupation"
                      value={editData.occupation || ""}
                      onChange={(e) => setEditData({ ...editData, occupation: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Location
                    </Label>
                    <Input
                      data-testid="edit-location"
                      value={editData.location || ""}
                      onChange={(e) => setEditData({ ...editData, location: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Email
                    </Label>
                    <Input
                      type="email"
                      data-testid="edit-email"
                      value={editData.email || ""}
                      onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                    />
                  </div>
                </>
              ) : (
                <>
                  {member.occupation && (
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                      <Briefcase className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Occupation</p>
                        <p className="font-medium text-foreground">{member.occupation}</p>
                      </div>
                    </div>
                  )}
                  {member.location && (
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                      <MapPin className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Location</p>
                        <p className="font-medium text-foreground">{member.location}</p>
                      </div>
                    </div>
                  )}
                  {member.email && (
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
                      <Mail className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Email</p>
                        <p className="font-medium text-foreground">{member.email}</p>
                      </div>
                    </div>
                  )}
                  {!member.occupation && !member.location && !member.email && (
                    <p className="text-muted-foreground text-center py-4">No additional information</p>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Bio & History Card */}
          <Card className="bg-card rounded-2xl border border-border/60 shadow-sm">
            <CardHeader>
              <CardTitle className="font-display text-xl">Biography & History</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {editing ? (
                <>
                  <div className="space-y-2">
                    <Label>Biography</Label>
                    <Textarea
                      data-testid="edit-bio"
                      value={editData.bio || ""}
                      onChange={(e) => setEditData({ ...editData, bio: e.target.value })}
                      rows={4}
                      placeholder="Share their story..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4" /> Historical Notes
                    </Label>
                    <Textarea
                      data-testid="edit-historical-notes"
                      value={editData.historical_notes || ""}
                      onChange={(e) => setEditData({ ...editData, historical_notes: e.target.value })}
                      rows={4}
                      placeholder="Important historical context..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Relationships / Marriages</Label>
                    <Textarea
                      data-testid="edit-relationships"
                      value={editData.relationships || ""}
                      onChange={(e) => setEditData({ ...editData, relationships: e.target.value })}
                      rows={2}
                      placeholder="e.g., Married to Jane (1960-1975), Married to Mary (1976-1990)"
                    />
                  </div>
                </>
              ) : (
                <>
                  {member.bio && (
                    <div className="p-4 rounded-xl bg-muted/50">
                      <p className="text-sm text-muted-foreground mb-2">Biography</p>
                      <p className="text-foreground leading-relaxed">{member.bio}</p>
                    </div>
                  )}
                  {member.relationships && (
                    <div className="p-4 rounded-xl bg-primary/5 border border-primary/20">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-primary">💍</span>
                        <p className="text-sm font-medium text-primary">Relationships / Marriages</p>
                      </div>
                      <p className="text-foreground leading-relaxed">{member.relationships}</p>
                    </div>
                  )}
                  {member.historical_notes && (
                    <div className="p-4 rounded-xl bg-accent/5 border border-accent/20">
                      <div className="flex items-center gap-2 mb-2">
                        <BookOpen className="w-4 h-4 text-accent" />
                        <p className="text-sm font-medium text-accent">Historical Notes</p>
                      </div>
                      <p className="text-foreground leading-relaxed">{member.historical_notes}</p>
                    </div>
                  )}
                  {!member.bio && !member.historical_notes && !member.relationships && (
                    <p className="text-muted-foreground text-center py-4">No biography or historical notes yet</p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Generation Info */}
        <Card className="bg-card rounded-2xl border border-border/60 shadow-sm mt-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Generation</p>
                <p className="text-2xl font-bold text-foreground">
                  {member.generation} - {GENERATION_LABELS[member.generation] || `Generation ${member.generation}`}
                </p>
              </div>
              {editing && (
                <div className="space-y-2">
                  <Label>Generation</Label>
                  <Select 
                    value={String(editData.generation)} 
                    onValueChange={(v) => setEditData({ ...editData, generation: parseInt(v) })}
                  >
                    <SelectTrigger data-testid="edit-generation-select" className="w-64">
                      <SelectValue placeholder="Select generation" />
                    </SelectTrigger>
                    <SelectContent>
                      {GENERATION_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Parents Section */}
        {(father || mother || editing) && (
          <Card className="bg-card rounded-2xl border border-border/60 shadow-sm mt-6">
            <CardHeader>
              <CardTitle className="font-display text-xl flex items-center gap-2">
                <Heart className="w-5 h-5 text-primary" /> Parents
              </CardTitle>
            </CardHeader>
            <CardContent>
              {editing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Father</Label>
                    <Select 
                      value={editData.parent_member_id || "none"} 
                      onValueChange={(v) => setEditData({ ...editData, parent_member_id: v === "none" ? "" : v })}
                    >
                      <SelectTrigger data-testid="edit-father-select">
                        <SelectValue placeholder="Select father" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {allMembers.filter(m => m.member_id !== memberId).map((m) => (
                          <SelectItem key={m.member_id} value={m.member_id}>
                            {m.first_name} {m.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Mother</Label>
                    <Select 
                      value={editData.mother_member_id || "none"} 
                      onValueChange={(v) => setEditData({ ...editData, mother_member_id: v === "none" ? "" : v })}
                    >
                      <SelectTrigger data-testid="edit-mother-select">
                        <SelectValue placeholder="Select mother" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {allMembers.filter(m => m.member_id !== memberId).map((m) => (
                          <SelectItem key={m.member_id} value={m.member_id}>
                            {m.first_name} {m.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {father && (
                    <div 
                      className="flex items-center gap-3 p-4 rounded-xl bg-muted/50 cursor-pointer hover:bg-muted/80 transition-colors"
                      onClick={() => navigate(`/member/${father.member_id}`)}
                      data-testid="father-link"
                    >
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={father.photo_url} />
                        <AvatarFallback className="bg-secondary text-secondary-foreground">
                          {father.first_name?.charAt(0)}{father.last_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-xs text-muted-foreground">Father</p>
                        <p className="font-medium text-foreground">{father.first_name} {father.last_name}</p>
                      </div>
                    </div>
                  )}
                  {mother && (
                    <div 
                      className="flex items-center gap-3 p-4 rounded-xl bg-muted/50 cursor-pointer hover:bg-muted/80 transition-colors"
                      onClick={() => navigate(`/member/${mother.member_id}`)}
                      data-testid="mother-link"
                    >
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={mother.photo_url} />
                        <AvatarFallback className="bg-secondary text-secondary-foreground">
                          {mother.first_name?.charAt(0)}{mother.last_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-xs text-muted-foreground">Mother</p>
                        <p className="font-medium text-foreground">{mother.first_name} {mother.last_name}</p>
                      </div>
                    </div>
                  )}
                  {!father && !mother && (
                    <p className="text-muted-foreground col-span-2 text-center py-2">No parents linked yet</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Children Section */}
        {children.length > 0 && (
          <Card className="bg-card rounded-2xl border border-border/60 shadow-sm mt-6">
            <CardHeader>
              <CardTitle className="font-display text-xl flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" /> Children ({children.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {children.map((child) => {
                  // Check if this member is the father or mother of the child
                  const relationship = child.parent_member_id === memberId ? "Father" : "Mother";
                  // Find the other parent
                  const otherParentId = child.parent_member_id === memberId ? child.mother_member_id : child.parent_member_id;
                  const otherParent = otherParentId ? allMembers.find(m => m.member_id === otherParentId) : null;
                  
                  return (
                    <div 
                      key={child.member_id}
                      className="flex items-center gap-3 p-4 rounded-xl bg-muted/50 cursor-pointer hover:bg-muted/80 transition-colors"
                      onClick={() => navigate(`/member/${child.member_id}`)}
                      data-testid={`child-${child.member_id}`}
                    >
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={child.photo_url} />
                        <AvatarFallback className="bg-secondary text-secondary-foreground text-sm">
                          {child.first_name?.charAt(0)}{child.last_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">{child.first_name} {child.last_name}</p>
                        {otherParent && (
                          <p className="text-xs text-muted-foreground truncate">
                            with {otherParent.first_name} {otherParent.last_name}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default MemberPage;
